import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import equipmentApi from '~/apis/equipment';
import utilityApi from '~/apis/utility';
import { CloseIcon, DeleteBin6LineIcon, EditLineIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { EQUIPMENT_NAMES } from '~/utils/constants/equipment';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IEditEquipment, IEquipment, ISensorPort } from '~/utils/interface/equipment';
import { IMasterDataItem } from '~/utils/interface/master';
import equipmentValidation from '~/validation/equipment';
import EditSensorDialog from '../edit-sensor-dialog/EditSensorDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetSensorTypes } from '~/thunks/master/masterThunk';
import useLanguage from '~/hooks/useLanguage';
import { handleGetAuthUserInfo } from '~/thunks/auth/authThunk';
import { useLocation } from 'react-router-dom';

type EditEquipmentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IEquipment;
  mode: string;
  agent?: { username: string };
};

const initialValues: IEditEquipment = {
  device_code: '',
  device_name: '',
  agent: '',
};

const EditEquipmentDialog = (props: EditEquipmentDialogProps) => {
  const { t } = useTranslation('admin/equipment');
  const dispatch = useAppDispatch();
  const location = useLocation();
  const language = useLanguage();
  const { sensorTypes } = useAppSelector((state) => state.master);
  const { authUser } = useAppSelector((state) => state.auth);
  const [agentOptions, setAgentOptions] = useState<IMasterDataItem[]>([]);
  const deviceNameOptions = useMemo(() => {
    return EQUIPMENT_NAMES.map((value) => ({
      id: t(`equipmentNameOptions.${value}`),
      name: t(`equipmentNameOptions.${value}`),
    }));
  }, [t]);
  const [isOpenEditSensorDialog, setIsOpenEditSensorDialog] = useState<boolean>(false);
  const [editSensorDialogMode, setEditSensorDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [sensor, setSensor] = useState<ISensorPort>();
  const [isOpenDeleteSensorDialog, setIsOpenDeleteSensorDialog] = useState<boolean>(false);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: equipmentValidation(t).create,
    initialValues,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await equipmentApi.createEquipment(values);
          notify('success', t('editEquipmentDialog.addSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        try {
          await equipmentApi.updateEquipment({
            ...values,
            location_id: props?.data?.location_id || 0,
            device_id: props?.data?.device_id || 0,
          });
          notify('success', t('editEquipmentDialog.updateSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListAgents();
        setAgentOptions(
          response.data.map((item) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name,
            };
          })
        );
        if (
          authUser?.permissions?.[0] === 'agent' &&
          location.pathname.indexOf('/admin/equipment') > -1 &&
          props.mode === DIALOG_MODE_CREATE
        ) {
          form.setFieldValue('agent', response.data?.[0].username);
        }
      } catch (error) {
        handleApiError(error);
      }

      dispatch(handleGetSensorTypes());
      dispatch(handleGetAuthUserInfo());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.open && props.mode === DIALOG_MODE_EDIT && props.data) {
      form.setValues({
        device_code: cast(props.data?.device_code),
        device_name: cast(props.data?.device_name),
        agent: props.agent?.username ?? cast(props.data?.agent),
      });
    }
    if (props.agent && props.mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('agent', props.agent?.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.agent]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleSetIsOpenEditSensorDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditSensorDialog(isOpen);
    if (needRefresh && props.setOpen) {
      props.setOpen(true, needRefresh);
    }
  };

  const handleDeleteSensor = async (row: ISensorPort | undefined) => {
    if (!row) return;

    try {
      await equipmentApi.deleteSensor({ device_code: props?.data?.device_code, sensor_code: row?.sensor_code });
      notify('success', t('deleteSensorDialog.deleteSuccess'));
      if (props.setOpen) props.setOpen(true, true);
      setIsOpenDeleteSensorDialog(false);
    } catch (e: any) {
      handleApiError(e);
    }
  };

  const getSensorTypeName = (id: number) => {
    const sensorType = sensorTypes.find((item) => Number(item.id) === id);
    if (!sensorType) return;
    return language.getValue('string_value', sensorType);
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_EDIT ? t('editEquipmentDialog.editTitle') : t('editEquipmentDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('editEquipmentDialog.deviceCode')}</InputLabel>
                {' : '}
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                name='device_code'
                placeholder={t('editEquipmentDialog.deviceCodePlaceholder')}
                value={form.values?.device_code}
                onChange={form.handleChange}
                error={Boolean(form.errors?.device_code)}
                helperText={form.errors?.device_code}
              />
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2'>{t('editEquipmentDialog.deviceName')}:</InputLabel>
              <Box className='w-1/2'>
                <BaseSelect
                  size='medium'
                  options={deviceNameOptions}
                  value={form.values.device_name}
                  placeholder={t('select')}
                  name='device_name'
                  isSearchEnable
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.device_name)}
                  helperText={form.errors?.device_name}
                  displayInvalidValue
                />
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2'>{t('agency')}</InputLabel>
              <Box className='w-1/2'>
                <BaseSelect
                  size='medium'
                  options={agentOptions}
                  value={form.values.agent}
                  placeholder={t('select')}
                  name='agent'
                  isSearchEnable
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.agent)}
                  helperText={form.errors?.agent}
                  displayInvalidValue
                  disabled={
                    props.agent?.username &&
                    props.mode === DIALOG_MODE_CREATE &&
                    agentOptions.find((item) => item.id === props.agent?.username)
                      ? true
                      : false
                  }
                />
              </Box>
            </BaseRow>
            {props.mode === DIALOG_MODE_EDIT ? (
              <Box className='mb-[26px]'>
                <BaseRow className='mb-[5px] gap-[15px]'>
                  <Box className='font-bold text-[#005F73]'>{t('editEquipmentDialog.sensorList')}</Box>
                  <Button
                    color='primary'
                    variant='contained'
                    className='font-bold rounded-full h-[30px] min-w-[96px] ml-auto'
                    onClick={() => {
                      setEditSensorDialogMode(DIALOG_MODE_CREATE);
                      setIsOpenEditSensorDialog(true);
                    }}
                  >
                    {t('editEquipmentDialog.assign')}
                  </Button>
                  <Box className='font-bold rounded-full text-[#FFFFFF] bg-[#005F73] w-[30px] h-[30px] flex justify-center items-center'>
                    {props.data?.sensor_ports?.length || 0}
                  </Box>
                </BaseRow>
                {props.data?.sensor_ports?.length ? (
                  <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {props.data.sensor_ports.map((row, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.sensor_code}</Box>
                                  <Box>{getSensorTypeName(row.sensor_type)}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none w-[120px]'>
                                <BaseRow>
                                  <IconButton
                                    onClick={() => {
                                      setSensor(row);
                                      setEditSensorDialogMode(DIALOG_MODE_EDIT);
                                      handleSetIsOpenEditSensorDialog(true);
                                    }}
                                  >
                                    <EditLineIcon className='[&>path]:fill-[#D9D9D9]' />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => {
                                      setSensor(row);
                                      setIsOpenDeleteSensorDialog(true);
                                    }}
                                  >
                                    <DeleteBin6LineIcon fill='#ED1002' />
                                  </IconButton>
                                </BaseRow>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
            ) : null}

            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => form.handleSubmit()}
            >
              {props.mode === DIALOG_MODE_EDIT ? t('editEquipmentDialog.save') : t('editEquipmentDialog.add')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <EditSensorDialog
        open={isOpenEditSensorDialog}
        mode={editSensorDialogMode}
        setOpen={handleSetIsOpenEditSensorDialog}
        equipment={props.data}
        data={sensor}
      />
      <ConfirmDialog
        title={t('deleteSensorDialog.title')}
        description={t('deleteSensorDialog.description', {
          sensorCode: sensor?.sensor_code,
        })}
        open={isOpenDeleteSensorDialog}
        onConfirm={() => handleDeleteSensor(sensor)}
        onCancel={() => setIsOpenDeleteSensorDialog(false)}
      />
    </>
  );
};

export default memo(EditEquipmentDialog);
